import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import {
  PlayerSearchViewAttributes,
  type TeamModeOverride as TeamModeOverrideValue,
} from '@sorare/core/src/lib/playerSearchView';

import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';
import {
  type TeamMode,
  useDerivedTeamMode,
} from '#src/searchCommonPlayer/teamMode.ts';

const manualLabels = defineMessages<Exclude<TeamModeOverrideValue, 'AUTO'>>({
  ALL: { defaultMessage: 'All' },
  CLUB: { defaultMessage: 'Club' },
  NATIONAL_TEAM: { defaultMessage: 'National team' },
});

const autoLabels = defineMessages<TeamMode>({
  ALL: { defaultMessage: 'Auto (All)' },
  CLUB: { defaultMessage: 'Auto (Club)' },
  NATIONAL_TEAM: { defaultMessage: 'Auto (National team)' },
});

export const TeamModeOverride = () => {
  const { formatMessage } = useIntl();
  const { statsManager, updatePlayerSearchViewAttribute } =
    useManagePlayerSearchView();
  const value =
    statsManager[PlayerSearchViewAttributes.TEAM_MODE_OVERRIDE] ?? 'AUTO';

  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const derived = useDerivedTeamMode(!!routeSlug);

  const options: {
    value: TeamModeOverrideValue;
    label: string;
  }[] = [
    { value: 'AUTO', label: formatMessage(autoLabels[derived]) },
    { value: 'ALL', label: formatMessage(manualLabels.ALL) },
    { value: 'CLUB', label: formatMessage(manualLabels.CLUB) },
    {
      value: 'NATIONAL_TEAM',
      label: formatMessage(manualLabels.NATIONAL_TEAM),
    },
  ];

  return (
    <Vertical gap={1}>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage defaultMessage="Team scope" />
      </LabelM>
      <DropdownSelect<TeamModeOverrideValue>
        size="small"
        value={value}
        options={options}
        onChange={next =>
          updatePlayerSearchViewAttribute(
            PlayerSearchViewAttributes.TEAM_MODE_OVERRIDE,
            next
          )
        }
      />
    </Vertical>
  );
};
